import { motion, AnimatePresence } from 'framer-motion';
import { X, Flame, Leaf, Star, ExternalLink, Droplet } from 'lucide-react';
import { useEffect } from 'react';

const SWIGGY_URL = process.env.REACT_APP_SWIGGY_URL;

const getItemDetails = (item) => {
  const isCooler = item.id.includes('panna') || item.id.includes('shikanji') || 
                   item.id.includes('paan') || item.id.includes('thandai') || 
                   item.id.includes('nimbu');
  
  const defaultIngredients = {
    'dahi-puri': ['Crispy Puris', 'Yogurt', 'Tamarind Chutney', 'Green Chutney', 'Sev', 'Coriander'],
    'dahi-papdi': ['Papdis', 'Yogurt', 'Tamarind Chutney', 'Green Chutney', 'Sev', 'Spices'],
    'raj-kachori': ['Large Kachori', 'Potatoes', 'Sprouts', 'Yogurt', 'Chutneys', 'Sev'],
    'boondi-raita': ['Boondi', 'Spiced Yogurt', 'Fresh Herbs', 'Cumin', 'Mint'],
    'aloo-tikki': ['Potato Patties', 'Yogurt', 'Tamarind Chutney', 'Green Chutney', 'Chickpeas'],
    'special-mix': ['Puris', 'Papdis', 'Potatoes', 'Yogurt', 'Sprouts', 'Chutneys', 'Sev'],
    'pani-puri-platter': ['Crispy Puris', 'Spicy Water', 'Potato Filling', 'Chickpeas', 'Tamarind'],
    'mixed-fruit': ['Seasonal Fruits', 'Chaat Masala', 'Lemon', 'Black Salt', 'Mint'],
    'mango-zaika': ['Fresh Mango', 'Mint', 'Chaat Masala', 'Lemon', 'Black Salt'],
    'pineapple-mint': ['Pineapple', 'Fresh Mint', 'Lime', 'Black Salt', 'Chaat Masala'],
    'watermelon-masala': ['Watermelon', 'Black Salt', 'Chaat Masala', 'Lemon', 'Mint'],
    'papaya-watermelon': ['Papaya', 'Watermelon', 'Pomegranate', 'Chaat Masala', 'Mint'],
    'corn-pomegranate': ['Sweet Corn', 'Pomegranate', 'Onions', 'Lemon', 'Spices'],
    'sprouts-protein': ['Moong Sprouts', 'Vegetables', 'Peanuts', 'Puffed Rice', 'Chutneys'],
    'sweet-corn': ['Boiled Corn', 'Butter', 'Chaat Masala', 'Lemon', 'Coriander'],
    'cucumber-mint': ['Cucumber', 'Mint Yogurt', 'Roasted Cumin', 'Lemon', 'Black Salt'],
    'moong-dal': ['Moong Dal', 'Onions', 'Tomatoes', 'Green Chili', 'Lemon', 'Spices'],
    'rainbow-veg': ['Mixed Vegetables', 'Nuts', 'Fruits', 'Yogurt', 'Chutneys'],
    'chana-chaat': ['Boiled Chickpeas', 'Onions', 'Tomatoes', 'Chutneys', 'Sev'],
    'boiled-peanut': ['Boiled Peanuts', 'Vegetables', 'Pomegranate', 'Lemon', 'Spices'],
    'aam-panna': ['Raw Mango', 'Mint', 'Cumin', 'Black Salt', 'Sugar'],
    'rose-basil': ['Lemon', 'Rose Water', 'Basil', 'Sugar', 'Black Salt'],
    'paan-shots': ['Paan Leaves', 'Gulkand', 'Fennel', 'Cardamom', 'Rose Water'],
    'thandai-chiller': ['Almonds', 'Pistachios', 'Milk', 'Saffron', 'Cardamom', 'Rose Petals'],
    'nimbu-shikanji': ['Fresh Lemon', 'Mint', 'Cumin', 'Black Salt', 'Sugar'],
    'paan-chia-cooler': ['Paan Extract', 'Chia Seeds', 'Rose Water', 'Basil', 'Mint']
  };

  const healthBenefits = isCooler ? [
    'Hydrating and refreshing',
    'Natural ingredients',
    'Aids digestion',
    'Rich in vitamins'
  ] : [
    'Rich in probiotics from fresh yogurt',
    'High in fiber and protein',
    'Made with fresh, natural ingredients',
    'Balanced mix of nutrients'
  ];

  return {
    isCooler,
    ingredients: defaultIngredients[item.id] || ['Fresh Ingredients', 'Traditional Spices', 'Homemade Chutneys'],
    healthBenefits,
    spiceLevel: isCooler ? 0 : (item.price > 200 ? 2 : item.price > 150 ? 2 : 1),
    rating: (4.2 + Math.random() * 0.7).toFixed(1),
    ordersToday: Math.floor(50 + Math.random() * 150),
    prepTime: isCooler ? '5-10 mins' : '15-20 mins',
    serves: isCooler ? '1 person' : item.price > 200 ? '2 people' : '1-2 people'
  };
};

export const MenuItemModal = ({ item, isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!item) return null;

  const details = getItemDetails(item);
  const isAvailable = item.available !== false;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
            data-testid="modal-overlay"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <div
              className="bg-cream rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="menu-item-modal"
            >
              <div className="relative">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all hover:scale-110"
                  data-testid="modal-close-btn"
                >
                  <X size={24} className="text-brown-dark" />
                </button>

                <div className="relative h-80 overflow-hidden rounded-t-2xl">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brown-dark/80 to-transparent p-6">
                    <div className="flex items-center gap-2 mb-2">
                      {item.tags && item.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="bg-turmeric text-brown-dark px-3 py-1 rounded-full text-xs font-bold"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  <div className="mb-4">
                    <h2 className="text-3xl font-playfair font-bold text-brown-dark mb-3">
                      {item.name}
                    </h2>
                    {!isAvailable && (
                      <div className="bg-brown-dark/10 border-2 border-brown-dark/30 rounded-lg px-4 py-2 mb-3">
                        <p className="text-brown-dark font-bold text-sm">⚠️ Currently Unavailable</p>
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-sm text-brown-medium">
                      <div className="flex items-center gap-1">
                        <Star size={16} className="text-turmeric fill-turmeric" />
                        <span className="font-bold text-brown-dark">{details.rating}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mb-6 pb-6 border-b border-brown-light/20">
                    {!details.isCooler && details.spiceLevel > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-brown-dark">Spice Level:</span>
                        <div className="flex gap-1">
                          {[...Array(3)].map((_, i) => (
                            <Flame
                              key={i}
                              size={16}
                              className={i < details.spiceLevel ? 'text-spice fill-spice' : 'text-brown-light'}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {details.isCooler && (
                      <div className="flex items-center gap-2">
                        <Droplet size={16} className="text-blue-500" />
                        <span className="text-sm font-medium text-brown-dark">Refreshing Cooler</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Leaf size={16} className="text-leaf" />
                      <span className="text-sm font-medium text-brown-dark">100% Vegetarian</span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-lg font-playfair font-bold text-brown-dark mb-3">
                      About this {details.isCooler ? 'drink' : 'dish'}
                    </h3>
                    <p className="text-brown-medium leading-relaxed mb-4">
                      {item.description}
                    </p>
                    <div className="bg-cream-secondary rounded-xl p-4">
                      <h4 className="font-bold text-brown-dark mb-2 text-sm">Key Ingredients:</h4>
                      <div className="flex flex-wrap gap-2">
                        {details.ingredients.map((ingredient, idx) => (
                          <span
                            key={idx}
                            className="bg-white text-brown-dark px-3 py-1 rounded-full text-xs border border-brown-light/20"
                          >
                            {ingredient}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="bg-leaf/10 border-2 border-leaf/20 rounded-xl p-4 mb-6">
                    <h4 className="font-bold text-brown-dark mb-2 flex items-center gap-2">
                      <Leaf size={18} className="text-leaf" />
                      Health Benefits
                    </h4>
                    <ul className="text-sm text-brown-medium space-y-1">
                      {details.healthBenefits.map((benefit, idx) => (
                        <li key={idx}>• {benefit}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex gap-4">
                    {isAvailable ? (
                      <a
                        href={SWIGGY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-spice text-white px-8 py-4 rounded-full hover:bg-spice-hover transition-all transform hover:-translate-y-1 hover:shadow-xl font-bold text-lg text-center flex items-center justify-center gap-2"
                        data-testid="modal-order-now-btn"
                      >
                        Order Now on Swiggy
                        <ExternalLink size={20} />
                      </a>
                    ) : (
                      <button
                        disabled
                        className="flex-1 bg-gray-400 text-white px-8 py-4 rounded-full cursor-not-allowed font-bold text-lg text-center opacity-60"
                        data-testid="modal-order-now-btn-disabled"
                      >
                        Currently Unavailable
                      </button>
                    )}
                  </div>

                  <p className="text-center text-sm text-brown-medium mt-4">
                    Estimated prep time: {details.prepTime} • Serves {details.serves}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};